import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Card, Tooltip } from 'reactstrap';
import moment from 'moment';
import axios from 'axios';
import { useRiskyIdNumbers, SortOrder } from '../hooks/useRiskyIdNumbers';
import { useSearchDebounce } from '../hooks/useSearchDebounce';
import useDebouncedLoading from '../hooks/useDebouncedLoading';
import { useAuditTrail } from '../hooks/useAuditTrail';
import { Pager } from '../../../../components/pager';
import { SearchInput } from '../../../../components/SearchInput';
import { DataTable, TableColumn } from '../../../../components/DataTable';
import { EmptyState } from '../../../../components/EmptyState';
import AddRiskyIdForm from './AddRiskyIdForm';
import EditRiskyIdForm from './EditRiskyIdForm';
import { ConfirmationDialog } from '../../../../components/ConfirmationDialog';
import AuditTrail from './AuditTrail';
import { Pill, PillVariant } from '../../../../components/Pill';
import { environment } from '../../../../environment/environment';
import toast from '../../../../utils/toast';
import styles from './RiskyIdNumbersTab.module.css';

const RiskyIdNumbersTab: React.FC = () => {
  const location = useLocation();
  const initialSearch = useMemo(() => new URLSearchParams(location.search).get('search') ?? '', []);
  const [page, setPage] = useState<number>(1);
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<any>(null);
  const [sortByField, setSortByField] = useState<string>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.Desc);
  const [expandedRows, setExpandedRows] = useState<Set<string | number>>(new Set());
  const [expandedRowIdNumber, setExpandedRowIdNumber] = useState<string | null>(null);
  const [activeOnly, setActiveOnly] = useState<boolean>(true);
  const [tooltipOpen, setTooltipOpen] = useState<{ [key: string]: boolean }>({});
  const { searchQuery, debouncedValue, setSearchQuery } = useSearchDebounce(initialSearch, 500);
  const { data, loading, totalPages, refetch } = useRiskyIdNumbers(debouncedValue, page, sortByField, sortOrder, activeOnly);
  const debouncedLoading = useDebouncedLoading(loading, 300);
  const { data: auditData, loading: auditLoading } = useAuditTrail(expandedRowIdNumber);

  useEffect(() => {
    setTooltipOpen({});
  }, [data]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (page !== 1) {
      setPage(1);
    }
    setExpandedRows(new Set());
    setExpandedRowIdNumber(null);
  };

  const handleActiveOnlyToggle = () => {
    setActiveOnly(prev => !prev);
    if (page !== 1) {
      setPage(1);
    }
    setExpandedRows(new Set());
    setExpandedRowIdNumber(null);
  };

  const handleAddSuccess = () => {
    setSearchQuery(''); // Clear search to show new item
    refetch();
    setPage(1); // Go back to first page to see the new item
  };

  const handleDateSort = () => {
    const newOrder = sortOrder === SortOrder.Desc ? SortOrder.Asc : SortOrder.Desc;
    setSortOrder(newOrder);
    setSortByField('created_at');
    setPage(1); // Reset to first page when sorting changes
  };

  const toggleTooltip = (tooltipId: string) => {
    setTooltipOpen(prev => ({
      ...prev,
      [tooltipId]: !prev[tooltipId]
    }));
  };


  const handleEdit = (row: any) => {
    setEditingItem(row);
    setIsEditModalOpen(true);
    setExpandedRows(new Set());
    setExpandedRowIdNumber(null);
  };

  const handleEditSuccess = () => {
    refetch();
    setEditingItem(null);
  };

  const handleDeactivate = (row: any) => {
    setItemToDelete(row);
    setIsDeleteDialogOpen(true);
  };

  const handleActivate = async (row: any) => {
    setExpandedRows(new Set());
    setExpandedRowIdNumber(null);
    try {
      await axios.put(`${environment.baseUrl}/risky-id-numbers/${row.id}/update/`, {
        reason: row.reason,
        active: true
      });
      toast.success('Risky ID number activated successfully');
      refetch();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to activate risky ID number');
    }
  };

  const handleRowToggle = (rowId: string | number) => {
    const newExpandedRows = new Set(expandedRows);
    if (newExpandedRows.has(rowId)) {
      newExpandedRows.delete(rowId);
      setExpandedRowIdNumber(null);
    } else {
      newExpandedRows.clear(); // Only allow one expanded row at a time
      newExpandedRows.add(rowId);
      // Find the row data to get the ID number for audit trail
      const row = data.find(item => item.id === rowId);
      setExpandedRowIdNumber(row?.id_number || null);
    }
    setExpandedRows(newExpandedRows);
  };

  const renderExpandedRow = () => <AuditTrail data={auditData} loading={auditLoading} />;

  const getRowClassName = (row: any) => row.active === false ? styles.inactiveRow : '';

  const confirmDeactivate = async () => {
    if (!itemToDelete) return;

    try {
      await axios.put(`${environment.baseUrl}/risky-id-numbers/${itemToDelete.id}/update/`, {
        reason: itemToDelete.reason,
        active: false
      });
      toast.success('Risky ID number deactivated successfully');
      refetch();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to deactivate risky ID number');
    } finally {
      setItemToDelete(null);
    }
  };

  const columns: TableColumn[] = [
    {
      key: 'id_number',
      label: 'ID Number',
      width: '300px'
    },
    {
      key: 'reason',
      label: 'Reason',
      width: '300px',
      className: styles.reasonColumn,
      render: (value: any, row: any) => {
        if (!value) return '';
        const truncated = value.length > 100 ? `${value.substring(0, 100)}...` : value;
        const tooltipId = `reason-tooltip-${row.id}`;

        return (
          <div className={styles.reasonCell}>
            <span
              id={tooltipId}
              className={value.length > 100 ? styles.clickableText : ''}
            >
              {truncated}
            </span>
            {value.length > 100 && (
              <Tooltip
                target={tooltipId}
                isOpen={tooltipOpen[tooltipId] || false}
                toggle={() => toggleTooltip(tooltipId)}
                placement="top"
              >
                {value}
              </Tooltip>
            )}
          </div>
        );
      }
    },
    {
      key: 'created_at',
      label: (
        <button
          type="button"
          className={styles.sortableHeader}
          onClick={handleDateSort}
          aria-label={`Sort by date ${sortOrder === SortOrder.Desc ? 'ascending' : 'descending'}`}
        >
          Date <span className={styles.sortArrow}>{sortOrder === SortOrder.Desc ? '↓' : '↑'}</span>
        </button>
      ),
      render: (value: string) => moment(value).format('DD MMM YY HH:mm')
    },
    ...(!activeOnly ? [{
      key: 'active',
      label: 'Status',
      width: '80px',
      render: (value: any, row: any) => {
        const isActive = row.active !== false;
        return (
          <Pill variant={isActive ? PillVariant.Success : PillVariant.Danger}>
            {isActive ? 'Active' : 'Inactive'}
          </Pill>
        );
      }
    }] : []),
    {
      key: 'actions',
      label: 'Actions',
      width: '120px',
      render: (value: any, row: any) => {
        const editTooltipId = `edit-tooltip-${row.id}`;
        const deactivateTooltipId = `deactivate-tooltip-${row.id}`;
        const activateTooltipId = `activate-tooltip-${row.id}`;
        const isActive = row.active !== false;

        return (
          <div className={styles.actionButtonsContainer}>
            <button
              type="button"
              id={editTooltipId}
              className={`${styles.actionButton} ${styles.editButton}`}
              onClick={() => handleEdit(row)}
              aria-label="Edit risky ID"
            >
              <i className="fas fa-pen" />
            </button>
            <Tooltip
              target={editTooltipId}
              isOpen={tooltipOpen[editTooltipId] || false}
              toggle={() => toggleTooltip(editTooltipId)}
              placement="top"
            >
              Edit
            </Tooltip>

            {isActive ? (
              <>
                <button
                  type="button"
                  id={deactivateTooltipId}
                  className={`${styles.actionButton} ${styles.deactivateButton}`}
                  onClick={() => handleDeactivate(row)}
                  aria-label="Deactivate risky ID"
                >
                  <i className="fas fa-times" />
                </button>
                <Tooltip
                  target={deactivateTooltipId}
                  isOpen={tooltipOpen[deactivateTooltipId] || false}
                  toggle={() => toggleTooltip(deactivateTooltipId)}
                  placement="top"
                >
                  Deactivate
                </Tooltip>
              </>
            ) : (
              <>
                <button
                  type="button"
                  id={activateTooltipId}
                  className={`${styles.actionButton} ${styles.activateButton}`}
                  onClick={() => handleActivate(row)}
                  aria-label="Activate risky ID"
                >
                  <i className="fas fa-check" />
                </button>
                <Tooltip
                  target={activateTooltipId}
                  isOpen={tooltipOpen[activateTooltipId] || false}
                  toggle={() => toggleTooltip(activateTooltipId)}
                  placement="top"
                >
                  Activate
                </Tooltip>
              </>
            )}
          </div>
        );
      }
    }
  ];

  const emptyState = (
    <EmptyState
      icon="fas fa-flag"
      message="No id numbers have been flagged"
      iconColor="#ffc3cc"
      iconSize="0.75rem"
    />
  );

  return (
    <>
      <div className={styles.headerContainer}>
        <div className={styles.leftControls}>
          <SearchInput
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search ID Numbers"
          />
          <div className={styles.filterToggle}>
            <div className={styles.switchContainer}>
              <input
                className={styles.switchInput}
                type="checkbox"
                id="active-only-toggle"
                checked={activeOnly}
                onChange={handleActiveOnlyToggle}
                role="switch"
                aria-checked={activeOnly}
                aria-label="Show active flags only"
              />
              <label
                htmlFor="active-only-toggle"
                className={`${styles.toggleSwitch} ${activeOnly ? styles.active : ''}`}
              >
                <div className={`${styles.toggleSlider} ${activeOnly ? styles.active : ''}`} />
              </label>
            </div>
            <label htmlFor="active-only-toggle" className={styles.switchLabel}>
              Show active flags only
            </label>
          </div>
        </div>
        <button
          className={`btn btn-primary ${styles.addButton}`}
          onClick={() => setIsDrawerOpen(true)}
        >
          <i className="fas fa-plus mr-1" />
          Add ID Number
        </button>
      </div>

      <Card className="shadow border-0">
        <div className={`table-responsive ${styles.tableContainer}`}>
          <DataTable
            columns={columns}
            data={data}
            loading={debouncedLoading}
            emptyState={emptyState}
            skeletonRows={5}
            expandableRows
            expandedRowRender={renderExpandedRow}
            expandedRows={expandedRows}
            onRowToggle={handleRowToggle}
            getRowClassName={getRowClassName}
          />
        </div>
      </Card>

      {totalPages > 1 && (
        <div className={styles.pagerWrapper}>
          <div className={styles.pagerContainer}>
            <Pager
              currentPage={page}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <AddRiskyIdForm
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        onSuccess={handleAddSuccess}
      />

      <EditRiskyIdForm
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        riskyId={editingItem}
      />

      <ConfirmationDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDeactivate}
        message={`Are you sure you want to deactivate the risky ID number "${itemToDelete?.id_number}"? This will allow employees with this ID to access sensitive features.`}
        confirmText="Deactivate"
        showHeader={false}
        showCancel={false}
      />
    </>
  );
};

export default RiskyIdNumbersTab;